import React, { useMemo } from 'react';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { ZfTypography } from '@zafire/react';
import AGGridComponent from '../../../../components/AgGrid/AGGridComponent';
import { ProductHistoryItem } from 'pco-product-service-internal-consumer';
import { useGetServiceAgreementHistory } from '../../../../hooks/queries/privateBanking';
import { formatWallClockDateTime } from '../../../../utils/dateUtils';
import { getStatusLabel } from '../../privateBankingStatusLabels';

type RevisionHistoryBlockProps = {
  serviceAgreementId: string | undefined;
};

type HistoryRow = ProductHistoryItem & {
  rowId: string;
};

const PRODUCT_STATUS_ATTRIBUTE = 'Product status';

const formatHistoryValue = (params: ValueFormatterParams<HistoryRow>): string => {
  if (!params.value) {
    return '';
  }

  if (params.data?.attribute !== PRODUCT_STATUS_ATTRIBUTE) {
    return String(params.value);
  }

  return getStatusLabel(params.value);
};

const FILTER_PARAMS = {
  buttons: ['reset', 'apply'] as const,
  closeOnApply: true,
};

const getHistoryColumnDefs = (): ColDef<HistoryRow>[] => [
  {
    field: 'dateTime',
    headerName: 'Date / Time',
    sortable: true,
    resizable: true,
    filter: 'agDateColumnFilter',
    filterParams: FILTER_PARAMS,
    flex: 0,
    width: 170,
    cellRenderer: (params: { value: string | Date | undefined }) => formatWallClockDateTime(params.value),
  },
  {
    field: 'modifiedBy',
    headerName: 'User',
    sortable: true,
    resizable: true,
    filter: 'agSetColumnFilter',
    filterParams: FILTER_PARAMS,
    flex: 0,
    width: 210,
  },
  {
    field: 'attribute',
    headerName: 'Attribute',
    sortable: true,
    resizable: true,
    filter: 'agSetColumnFilter',
    filterParams: FILTER_PARAMS,
    flex: 0,
    width: 150,
  },
  {
    field: 'previousValue',
    headerName: 'Previous value',
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    filterParams: FILTER_PARAMS,
    flex: 0,
    width: 200,
    valueFormatter: formatHistoryValue,
  },
  {
    field: 'newValue',
    headerName: 'New value',
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter',
    filterParams: FILTER_PARAMS,
    flex: 0,
    width: 200,
    valueFormatter: formatHistoryValue,
  },
];

export const RevisionHistoryBlock: React.FC<RevisionHistoryBlockProps> = ({ serviceAgreementId }) => {
  const { data, isLoading, isError } = useGetServiceAgreementHistory(serviceAgreementId);

  const rows = useMemo<HistoryRow[]>(
    () =>
      (data?.productHistoryItems ?? []).map((item, index) => ({
        ...item,
        rowId: `${serviceAgreementId ?? 'history'}-${index}`,
      })),
    [data?.productHistoryItems, serviceAgreementId],
  );

  const columnDefs = useMemo(() => getHistoryColumnDefs(), []);

  if (isLoading) {
    return (
      <ZfTypography data-testid="product-revision-history-loading" variant="body-medium">
        Loading revision history...
      </ZfTypography>
    );
  }

  if (isError) {
    return (
      <ZfTypography data-testid="product-revision-history-error" variant="body-medium" color="danger">
        Revision history could not be loaded.
      </ZfTypography>
    );
  }

  if (rows.length === 0) {
    return (
      <ZfTypography data-testid="product-revision-history-empty" variant="body-medium">
        No revision history available yet.
      </ZfTypography>
    );
  }

  return (
    <AGGridComponent
      id="private-banking-revision-history-table"
      items={rows as unknown as Record<string, unknown>[]}
      getColumnDefs={columnDefs}
      rowKey="rowId"
      disableSelection={true}
      showPagination={false}
      suppressRowHoverHighlight={true}
      rowHeight={56}
      disableSizeColumnsToFit={true}
      disableColumnVirtualisation={true}
    />
  );
};
